import React from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircle, closeCircle, alertCircle, downloadOutline } from 'ionicons/icons';
import { parseTestSpec, formatTimestamp, analyzeExecution } from '../../utils/testSpecParser';
import './TestExecutionReport.css';

const TestExecutionReport = ({ execution, events, testInstructions, testResult, onJumpToTime }) => {
  if (!execution) {
    return <div className="ter-loading">Loading execution data...</div>;
  }

  const testSpec = parseTestSpec(testInstructions);
  const realAssertions = testResult?.assertions || [];
  const analysis = analyzeExecution(execution, events, testSpec, realAssertions);

  const actionEvents = events.filter(e => 
    e.type !== 'snapshot' && e.type !== 'close'
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PASSED':
        return <IonIcon icon={checkmarkCircle} className="ter-status-icon ter-icon-success" />;
      case 'FAILED':
        return <IonIcon icon={closeCircle} className="ter-status-icon ter-icon-error" />;
      default:
        return <IonIcon icon={alertCircle} className="ter-status-icon ter-icon-warning" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PASSED':
        return 'ter-status-passed';
      case 'FAILED':
        return 'ter-status-failed';
      default:
        return 'ter-status-running';
    }
  };

  const handleJumpToVideo = (videoOffsetMs) => {
    if (onJumpToTime && videoOffsetMs !== undefined) {
      onJumpToTime(videoOffsetMs / 1000);
      
      const videoSection = document.querySelector('.video-container');
      if (videoSection) {
        videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="test-execution-report">
      <div className={`ter-header ${getStatusClass(analysis.overallStatus)}`}>
        <div className="ter-header-left">
          {getStatusIcon(analysis.overallStatus)}
          <div className="ter-header-text">
            <h1 className="ter-title">
              {execution.testName || execution.name || 'Test Execution'}
            </h1>
            <p className="ter-subtitle">
              {formatTimeAgo(execution.timestamp || execution.createdAt)} • 
              {formatTimestamp(analysis.duration)} duration
            </p>
          </div>
        </div>
        <div className="ter-header-status">
          {analysis.overallStatus}
        </div>
      </div>

      <div className="ter-metrics">
        <div className="ter-metric-card">
          <div className="ter-metric-value">
            {analysis.successfulSteps}/{analysis.totalSteps}
          </div>
          <div className="ter-metric-label">Steps Executed</div>
          <div className="ter-metric-status">
            {analysis.failedSteps === 0 ? '✓ All successful' : `⚠️ ${analysis.failedSteps} failed`}
          </div>
        </div>

        <div className="ter-metric-card">
          <div className="ter-metric-value">
            {analysis.passedAssertions}/{analysis.expectedAssertions}
          </div>
          <div className="ter-metric-label">Assertions</div>
          <div className="ter-metric-status">
            {analysis.failedAssertions === 0 ? '✓ All verified' : `❌ ${analysis.failedAssertions} failed`}
          </div>
        </div>

        <div className="ter-metric-card">
          <div className="ter-metric-value">
            {analysis.errors}
          </div>
          <div className="ter-metric-label">Errors</div>
          <div className="ter-metric-status">
            {analysis.errors === 0 ? '✓ Clean run' : '⚠️ Review needed'}
          </div>
        </div>
      </div>

      {(realAssertions.length > 0 || (testSpec && testSpec.expectedResults.length > 0)) && (
        <div className="ter-section">
          <h2 className="ter-section-title">Assertions & Verifications</h2>
          <div className="ter-assertions">
            {realAssertions.length > 0 ? (
              realAssertions.map((assertion, index) => {
                let verificationEvent = null;
                
                if (assertion.verifiedAtEventId !== undefined) {
                  // Primary: direct event id lookup (set by result-handler enrichment)
                  verificationEvent = events.find(e => e.id === assertion.verifiedAtEventId);
                } else if (assertion.verifiedAfterAction !== undefined) {
                  // Legacy fallback: match by matchedActionIndex or closest preceding
                  verificationEvent = events.find(
                    e => e.matchedActionIndex === assertion.verifiedAfterAction
                  );
                  if (!verificationEvent) {
                    const enrichedEvents = events.filter(e => e.matchedActionIndex !== undefined);
                    let best = null;
                    for (const e of enrichedEvents) {
                      if (e.matchedActionIndex <= assertion.verifiedAfterAction) {
                        if (!best || e.matchedActionIndex > best.matchedActionIndex) {
                          best = e;
                        }
                      }
                    }
                    verificationEvent = best;
                  }
                } else {
                  const desc = (assertion.description || '').toLowerCase();
                  verificationEvent = events.find(e => {
                    const eventDesc = (e.description || e.reasoning || '').toLowerCase();
                    return (eventDesc.includes('redirect') && desc.includes('redirect')) ||
                           (eventDesc.includes('welcome') && desc.includes('welcome')) ||
                           (eventDesc.includes('login') && desc.includes('authenticated'));
                  });
                }

                return (
                  <div key={index} className={`ter-assertion ${assertion.passed ? 'ter-assertion-passed' : 'ter-assertion-failed'}`}>
                    <div className="ter-assertion-icon">
                      {assertion.passed ? '✓' : '✗'}
                    </div>
                    <div className="ter-assertion-content">
                      <div className="ter-assertion-text">
                        {assertion.description}
                        {assertion.evidence && (
                          <span className="ter-assertion-evidence"> — {assertion.evidence}</span>
                        )}
                      </div>
                      <div className="ter-assertion-meta">
                        {assertion.passed && <span className="ter-badge ter-badge-success">PASSED</span>}
                        {!assertion.passed && <span className="ter-badge ter-badge-error">FAILED</span>}
                        {verificationEvent && verificationEvent.videoOffsetMs !== undefined && (
                          <span className="ter-assertion-events">
                            Verified at: 
                            <button
                              className="ter-assertion-timestamp"
                              onClick={() => handleJumpToVideo(verificationEvent.videoOffsetMs)}
                              title={`Jump to: ${verificationEvent.description || verificationEvent.reasoning}`}
                            >
                              {formatTimestamp(verificationEvent.videoOffsetMs)}
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              testSpec.expectedResults.map((result, index) => {
                const isPassed = analysis.overallStatus === 'PASSED';
                const isSkipped = analysis.overallStatus === 'FAILED' && analysis.failedSteps > 0;
                
                return (
                  <div key={index} className={`ter-assertion ${isPassed ? 'ter-assertion-passed' : isSkipped ? 'ter-assertion-skipped' : 'ter-assertion-failed'}`}>
                    <div className="ter-assertion-icon">
                      {isPassed ? '✓' : isSkipped ? '❓' : '✗'}
                    </div>
                    <div className="ter-assertion-content">
                      <div className="ter-assertion-text">{result}</div>
                      <div className="ter-assertion-status">
                        {isPassed && <span className="ter-badge ter-badge-success">PASSED</span>}
                        {isSkipped && <span className="ter-badge ter-badge-warning">SKIPPED</span>}
                        {!isPassed && !isSkipped && <span className="ter-badge ter-badge-error">FAILED</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="ter-section">
        <h2 className="ter-section-title">Execution Timeline</h2>
        <div className="ter-timeline">
          {actionEvents.map((event, index) => {
            const hasError = event.error || event.status === 'failed';

            return (
              <div key={event.id} className={`ter-timeline-item ${hasError ? 'ter-item-error' : 'ter-item-success'}`}>
                <div className="ter-timeline-marker">
                  <div className={`ter-step-number ${hasError ? 'ter-error' : 'ter-success'}`}>
                    {index + 1}
                  </div>
                  {index < actionEvents.length - 1 && <div className="ter-timeline-connector" />}
                </div>
                
                <div className="ter-timeline-content">
                  <div className="ter-timeline-header">
                    {event.videoOffsetMs !== undefined ? (
                      <button 
                        className="ter-time ter-time-clickable"
                        onClick={() => handleJumpToVideo(event.videoOffsetMs)}
                        title="Click to jump to this moment in video"
                      >
                        {formatTimestamp(event.videoOffsetMs)}
                      </button>
                    ) : (
                      <span className="ter-time">
                        {formatTimestamp(event.videoOffsetMs)}
                      </span>
                    )}
                    <span className={`ter-event-status ${hasError ? 'ter-failed' : 'ter-passed'}`}>
                      {hasError ? '❌' : '✅'}
                    </span>
                    <span className="ter-description">
                      {event.description || event.reasoning || event.step || event.type}
                    </span>
                  </div>

                  {event.data && Object.keys(event.data).length > 0 && (
                    <div className="ter-event-details">
                      {event.data.url && (
                        <div className="ter-detail-item">
                          <span className="ter-detail-label">URL:</span>
                          <a href={event.data.url} target="_blank" rel="noopener noreferrer" className="ter-detail-link">
                            {event.data.url}
                          </a>
                        </div>
                      )}
                      {event.data.element && (
                        <div className="ter-detail-item">
                          <span className="ter-detail-label">Element:</span>
                          <code className="ter-detail-code">{event.data.element}</code>
                          {event.data.stableId && (
                            <code className="ter-detail-id">({event.data.stableId})</code>
                          )}
                        </div>
                      )}
                      {event.data.text && event.type === 'type' && (
                        <div className="ter-detail-item">
                          <span className="ter-detail-label">Text:</span>
                          <code className="ter-detail-code">{event.data.text}</code>
                        </div>
                      )}
                    </div>
                  )}

                  {event.error && (
                    <div className="ter-error-box">
                      <strong>Error:</strong> {event.error.message || event.error}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {execution.urls && (
        <div className="ter-section">
          <h2 className="ter-section-title">Downloads</h2>
          <div className="ter-downloads">
            {execution.urls.events && (
              <a href={execution.urls.events} download className="ter-download-btn">
                <IonIcon icon={downloadOutline} />
                events.json
              </a>
            )}
            {execution.urls.generatedTest && (
              <a href={execution.urls.generatedTest} download className="ter-download-btn">
                <IonIcon icon={downloadOutline} />
                generated-test.spec.js
              </a>
            )}
            {execution.urls.testInstructions && (
              <a href={execution.urls.testInstructions} download className="ter-download-btn">
                <IonIcon icon={downloadOutline} />
                test-instructions.txt
              </a>
            )}
            {execution.urls.video && (
              <a href={execution.urls.video} download className="ter-download-btn">
                <IonIcon icon={downloadOutline} />
                recording.webm
              </a>
            )}
          </div>
        </div>
      )}

      {execution.error && (
        <div className="ter-section">
          <h2 className="ter-section-title ter-error-title">Execution Error</h2>
          <div className="ter-error-card">
            <pre className="ter-error-message">{execution.error}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestExecutionReport;
